import { IJourney, IJourneyStep } from "config/types";
import { logout } from "store/actions/authentication";
import {
  clearJourneyActive,
  fetchJourney,
  finishJourney,
  restoreJourney,
  setJourneyActive,
  setJourneyActiveSection,
  setJourneyInactive,
  setJourneyProgressPercentOverride,
  setJourneyStep,
  setShowJourneyProgress,
} from "store/actions/journey";

import { createReducer } from "@reduxjs/toolkit";
import { defaultTo, filter, isEqual, uniq } from "lodash-es";
import store2 from "store2";

const { local } = store2;

export interface IJourneyWithActive extends IJourney {
  active: boolean;
  status?: {
    isLoaded: boolean;
  };
}

export type JourneyState = {
  collection: Record<string, IJourneyWithActive>;
  activeJourneys: string[];
  progress: {
    authenticatedAtStart: boolean;
    show: boolean;
    percentOverride?: number;
  };
  steps: {
    active: string;
    collection: Record<string, IJourneyStep>;
    ids: string[];
  };
  sections: {
    active: string;
  };
};

const defaultState = {
  collection: {} as Record<string, IJourneyWithActive>,
  activeJourneys: [] as string[],
  progress: {
    authenticatedAtStart: false as boolean,
    show: false as boolean,
    percentOverride: undefined as number | undefined,
  },
  steps: {
    active: "",
    collection: {} as Record<string, IJourneyStep>,
    ids: [] as string[],
  },
  sections: {
    active: "",
  },
} satisfies JourneyState;

const journey = createReducer(defaultState, builder => {
  builder.addCase(setJourneyStep, (state, { payload }) => {
    state.sections.active = "";
    state.steps.active = payload.id;
  });
  builder.addCase(setJourneyInactive, (state, { payload }) => {
    state.activeJourneys = uniq(filter(state.activeJourneys, i => !isEqual(i, payload.id)));
    state.collection[payload.id] = {
      ...state.collection[payload.id],
    };
  });
  builder.addCase(setJourneyActiveSection, (state, { payload }) => {
    state.sections.active = payload.section;
  });
  builder.addCase(setShowJourneyProgress, (state, { payload }) => {
    state.progress.show = payload.showJourneyProgress;
  });
  builder.addCase(setJourneyProgressPercentOverride, (state, { payload }) => {
    state.progress.percentOverride = payload.percent;
  });
  builder.addCase(finishJourney.fulfilled, () => defaultState);
  builder.addCase(fetchJourney.fulfilled, (state, { payload }) => {
    state.steps = {
      active: defaultTo(payload.steps.active, state.steps.active),
      collection: {
        ...state.steps.collection,
        ...payload.steps.collection,
      },
      ids: [...state.steps.ids, ...payload.steps.ids],
    };
    state.collection[payload.journey.id] = {
      ...state.collection[payload.journey.id],
      ...payload.journey,
      active: true,
      status: {
        isLoaded: true,
      },
    };
  });
  builder.addCase(setJourneyActive.fulfilled, (state, { payload }) => {
    state.activeJourneys = uniq([...state.activeJourneys, payload.id]);
    state.collection[payload.id] = {
      ...state.collection[payload.id],
    };
    state.progress.authenticatedAtStart = payload.authenticated;
  });
  builder.addCase(restoreJourney, (state, { payload }) => {
    // Semantically equivalent to `{ ...defaultState, ...state, ...payload.state }`.
    // The ...defaultState spread was a no-op in the original code (already the base),
    // and Immer requires mutation rather than returning a new object.
    Object.assign(state, payload.state);
  });
  builder.addCase(clearJourneyActive, () => {
    return defaultState;
  });
  builder.addCase(logout, () => {
    local.remove("completedJourneys");
    return defaultState;
  });
});

export default journey;
